export type SensorFormulaType = 'pt' | 'ni_din' | 'ni_tk5000' | 'ntc';

export type TemperatureSensorModel = {
  id: string;
  label: string;
  type: SensorFormulaType;
  baseResistanceOhm: number;
  minTempC: number;
  maxTempC: number;
  betaK?: number;
};

export const SENSOR_TYPE_LABELS: Record<SensorFormulaType, string> = {
  pt: 'PT',
  ni_din: 'NI DIN',
  ni_tk5000: 'NI TK5000',
  ntc: 'NTC'
};

const PT_A = 3.9083e-3;
const PT_B = -5.775e-7;
const PT_C = -4.183e-12;

const NI_DIN_A = 5.4963e-3;
const NI_DIN_B = 6.7556e-6;

const TK5000_ALPHA = 5e-3;
const T25_K = 298.15;

export const TEMPERATURE_SENSOR_MODELS: TemperatureSensorModel[] = [
  { id: 'pt100_star', label: 'PT100*', type: 'pt', baseResistanceOhm: 100, minTempC: -200, maxTempC: 850 },
  { id: 'pt200_star', label: 'PT200*', type: 'pt', baseResistanceOhm: 200, minTempC: -200, maxTempC: 850 },
  { id: 'pt500_star', label: 'PT500*', type: 'pt', baseResistanceOhm: 500, minTempC: -200, maxTempC: 850 },
  { id: 'pt1000', label: 'PT1000', type: 'pt', baseResistanceOhm: 1000, minTempC: -200, maxTempC: 850 },

  { id: 'ni1000_din_star', label: 'NI1000 DIN*', type: 'ni_din', baseResistanceOhm: 1000, minTempC: -60, maxTempC: 180 },
  { id: 'ni200_din_star', label: 'NI200 DIN*', type: 'ni_din', baseResistanceOhm: 200, minTempC: -60, maxTempC: 180 },
  { id: 'ni500_din_star', label: 'NI500 DIN*', type: 'ni_din', baseResistanceOhm: 500, minTempC: -60, maxTempC: 180 },
  { id: 'ni1000_din', label: 'NI1000 DIN', type: 'ni_din', baseResistanceOhm: 1000, minTempC: -60, maxTempC: 180 },

  { id: 'ni100_tk5000_star', label: 'NI100 TK5000*', type: 'ni_tk5000', baseResistanceOhm: 100, minTempC: -60, maxTempC: 180 },
  { id: 'ni200_tk5000_star', label: 'NI200 TK5000*', type: 'ni_tk5000', baseResistanceOhm: 200, minTempC: -60, maxTempC: 180 },
  { id: 'ni500_tk5000_star', label: 'NI500 TK5000*', type: 'ni_tk5000', baseResistanceOhm: 500, minTempC: -60, maxTempC: 180 },
  { id: 'ni1000_tk5000', label: 'NI1000 TK5000', type: 'ni_tk5000', baseResistanceOhm: 1000, minTempC: -60, maxTempC: 180 },

  { id: 'ntc_1k', label: 'NTC 1k', type: 'ntc', baseResistanceOhm: 1000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_1k8', label: 'NTC 1k8', type: 'ntc', baseResistanceOhm: 1800, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_2k', label: 'NTC 2k', type: 'ntc', baseResistanceOhm: 2000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_2k2', label: 'NTC 2k2', type: 'ntc', baseResistanceOhm: 2200, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_3k', label: 'NTC 3k', type: 'ntc', baseResistanceOhm: 3000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_4k7', label: 'NTC 4k7', type: 'ntc', baseResistanceOhm: 4700, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_5k', label: 'NTC 5k', type: 'ntc', baseResistanceOhm: 5000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_10k', label: 'NTC 10k', type: 'ntc', baseResistanceOhm: 10000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_12k', label: 'NTC 12k', type: 'ntc', baseResistanceOhm: 12000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_15k', label: 'NTC 15k', type: 'ntc', baseResistanceOhm: 15000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_20k', label: 'NTC 20k', type: 'ntc', baseResistanceOhm: 20000, betaK: 3950, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_3k3_stefa_ft_t30', label: 'NTC 3k3 Stefa FT-T30', type: 'ntc', baseResistanceOhm: 3300, betaK: 3977, minTempC: -40, maxTempC: 125 },
  { id: 'ntc_22k', label: 'NTC 22k', type: 'ntc', baseResistanceOhm: 22000, betaK: 3950, minTempC: -40, maxTempC: 125 }
];

export type ResistanceResult =
  | { ok: true; resistanceOhm: number; model: TemperatureSensorModel }
  | { ok: false; error: string; model?: TemperatureSensorModel };

export type TemperatureResult =
  | { ok: true; temperatureC: number; model: TemperatureSensorModel }
  | { ok: false; error: string; model?: TemperatureSensorModel };

const calculatePtResistance = (baseResistanceOhm: number, temperatureC: number): number => {
  if (temperatureC >= 0) {
    return baseResistanceOhm * (1 + PT_A * temperatureC + PT_B * temperatureC * temperatureC);
  }
  return (
    baseResistanceOhm *
    (1 +
      PT_A * temperatureC +
      PT_B * temperatureC * temperatureC +
      PT_C * (temperatureC - 100) * temperatureC * temperatureC * temperatureC)
  );
};

const calculateNiDinResistance = (baseResistanceOhm: number, temperatureC: number): number =>
  baseResistanceOhm * (1 + NI_DIN_A * temperatureC + NI_DIN_B * temperatureC * temperatureC);

const calculateNiTk5000Resistance = (baseResistanceOhm: number, temperatureC: number): number =>
  baseResistanceOhm * (1 + TK5000_ALPHA * temperatureC);

const calculateNtcResistance = (baseResistanceOhm: number, betaK: number, temperatureC: number): number => {
  const tempK = temperatureC + 273.15;
  return baseResistanceOhm * Math.exp(betaK * (1 / tempK - 1 / T25_K));
};

const calculateResistanceByModel = (model: TemperatureSensorModel, temperatureC: number): number | null => {
  switch (model.type) {
    case 'pt':
      return calculatePtResistance(model.baseResistanceOhm, temperatureC);
    case 'ni_din':
      return calculateNiDinResistance(model.baseResistanceOhm, temperatureC);
    case 'ni_tk5000':
      return calculateNiTk5000Resistance(model.baseResistanceOhm, temperatureC);
    case 'ntc':
      return calculateNtcResistance(model.baseResistanceOhm, model.betaK ?? 3950, temperatureC);
    default:
      return null;
  }
};

type SensorResistanceRange = {
  model: TemperatureSensorModel;
  resistanceAtMinTempOhm: number;
  resistanceAtMaxTempOhm: number;
  minResistanceOhm: number;
  maxResistanceOhm: number;
};

export const getSensorResistanceRange = (modelId: string): SensorResistanceRange | undefined => {
  const model = getSensorModelById(modelId);
  if (!model) return undefined;

  const resistanceAtMinTempOhm = calculateResistanceByModel(model, model.minTempC);
  const resistanceAtMaxTempOhm = calculateResistanceByModel(model, model.maxTempC);

  if (
    resistanceAtMinTempOhm === null ||
    resistanceAtMaxTempOhm === null ||
    !Number.isFinite(resistanceAtMinTempOhm) ||
    !Number.isFinite(resistanceAtMaxTempOhm) ||
    resistanceAtMinTempOhm <= 0 ||
    resistanceAtMaxTempOhm <= 0
  ) {
    return undefined;
  }

  return {
    model,
    resistanceAtMinTempOhm,
    resistanceAtMaxTempOhm,
    minResistanceOhm: Math.min(resistanceAtMinTempOhm, resistanceAtMaxTempOhm),
    maxResistanceOhm: Math.max(resistanceAtMinTempOhm, resistanceAtMaxTempOhm)
  };
};

const solveTemperatureByResistance = (model: TemperatureSensorModel, targetResistanceOhm: number): number | null => {
  const range = getSensorResistanceRange(model.id);
  if (!range) return null;

  if (targetResistanceOhm < range.minResistanceOhm || targetResistanceOhm > range.maxResistanceOhm) {
    return null;
  }

  const isIncreasing = range.resistanceAtMaxTempOhm >= range.resistanceAtMinTempOhm;
  let lowTemp = model.minTempC;
  let highTemp = model.maxTempC;
  const tolerance = Math.max(1e-6, targetResistanceOhm * 1e-8);

  for (let iteration = 0; iteration < 80; iteration += 1) {
    const midTemp = (lowTemp + highTemp) / 2;
    const midResistance = calculateResistanceByModel(model, midTemp);
    if (midResistance === null || !Number.isFinite(midResistance)) {
      return null;
    }

    const diff = midResistance - targetResistanceOhm;
    if (Math.abs(diff) <= tolerance) {
      return midTemp;
    }

    if (isIncreasing) {
      if (midResistance < targetResistanceOhm) {
        lowTemp = midTemp;
      } else {
        highTemp = midTemp;
      }
    } else if (midResistance > targetResistanceOhm) {
      lowTemp = midTemp;
    } else {
      highTemp = midTemp;
    }
  }

  return (lowTemp + highTemp) / 2;
};

export const getSensorModelById = (id: string): TemperatureSensorModel | undefined =>
  TEMPERATURE_SENSOR_MODELS.find((model) => model.id === id);

export const calculateSensorResistance = (modelId: string, temperatureC: number): ResistanceResult => {
  const model = getSensorModelById(modelId);
  if (!model) {
    return { ok: false, error: 'Unbekannter Fuehlertyp.' };
  }

  if (!Number.isFinite(temperatureC)) {
    return { ok: false, error: 'Ungueltiger Temperaturwert.', model };
  }

  if (temperatureC < model.minTempC || temperatureC > model.maxTempC) {
    return {
      ok: false,
      error: `Temperatur ausserhalb des gueltigen Bereichs (${model.minTempC} bis ${model.maxTempC} °C).`,
      model
    };
  }

  const resistanceOhm = calculateResistanceByModel(model, temperatureC);

  if (resistanceOhm === null) {
    return { ok: false, error: 'Fuer diesen Fuehlertyp ist keine Berechnung hinterlegt.', model };
  }

  if (!Number.isFinite(resistanceOhm) || resistanceOhm <= 0) {
    return { ok: false, error: 'Berechnung nicht moeglich.', model };
  }

  return { ok: true, resistanceOhm, model };
};

export const calculateSensorTemperature = (modelId: string, resistanceOhm: number): TemperatureResult => {
  const model = getSensorModelById(modelId);
  if (!model) {
    return { ok: false, error: 'Unbekannter Fuehlertyp.' };
  }

  if (!Number.isFinite(resistanceOhm) || resistanceOhm <= 0) {
    return { ok: false, error: 'Ungueltiger Widerstandswert.', model };
  }

  const range = getSensorResistanceRange(modelId);
  if (!range) {
    return { ok: false, error: 'Widerstandsbereich konnte nicht ermittelt werden.', model };
  }

  if (resistanceOhm < range.minResistanceOhm || resistanceOhm > range.maxResistanceOhm) {
    return {
      ok: false,
      error: `Widerstand ausserhalb des gueltigen Bereichs (${formatResistanceValue(range.minResistanceOhm)} bis ${formatResistanceValue(range.maxResistanceOhm)}).`,
      model
    };
  }

  const temperatureC = solveTemperatureByResistance(model, resistanceOhm);
  if (temperatureC === null || !Number.isFinite(temperatureC)) {
    return { ok: false, error: 'Berechnung nicht moeglich.', model };
  }

  if (temperatureC < model.minTempC - 1e-6 || temperatureC > model.maxTempC + 1e-6) {
    return { ok: false, error: 'Temperatur ausserhalb des gueltigen Bereichs.', model };
  }

  return { ok: true, temperatureC, model };
};

const resistanceFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

const kiloFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3
});

export const formatResistanceValue = (valueOhm: number): string => {
  if (!Number.isFinite(valueOhm)) return '-';
  if (valueOhm >= 1000) {
    return `${resistanceFormatter.format(valueOhm)} Ω (${kiloFormatter.format(valueOhm / 1000)} kΩ)`;
  }
  return `${resistanceFormatter.format(valueOhm)} Ω`;
};
